import { CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER, getBaseModelFromEditorModel } from '@core/shared/model';
import { getMetadataStorage, ValidationTypes, Validator } from 'class-validator';
import * as deepmerge from 'deepmerge';
import { map, of } from 'rxjs';
import { DynamicFormArray } from '../dynamic-form';
import { DynamicFormGroup } from '../dynamic-form/dynamic-form-group';
import { isAbstractControlOptions, isLegacyOrOpts, ValidationKeys } from '../models';
import { CustomValidationExecutor, getGlobalDynamicFormBuilderOptions, ValidationUtils } from '../utils';
export function prepareValidationMetadata(factoryModel, validatorOptions) {
  const groups = validatorOptions ? validatorOptions.groups : undefined;
  const strictGroups = validatorOptions && validatorOptions.strictGroups || false;
  const always = validatorOptions && validatorOptions.always || false;
  // Get the validation rules from the object decorators
  const allValidationMetadatas = getMetadataStorage().getTargetValidationMetadatas(factoryModel, '', always, strictGroups);
  // Get the validation rules for the validation group: https://github.com/typestack/class-validator#validation-groups
  const validationGroupMetadatas = getMetadataStorage().getTargetValidationMetadatas(factoryModel, '', always, strictGroups, groups);
  const classValidatorOptions = deepmerge(getGlobalDynamicFormBuilderOptions().classValidatorOptions, validatorOptions);
  const formGroupFields = {};
  const validator = new Validator();
  const customValidator = new CustomValidationExecutor(validator, classValidatorOptions);
  return {
    allValidationMetadatas,
    validationGroupMetadatas,
    formGroupFields,
    validator,
    customValidator
  };
}
export function getNgValidatorsFromConfig(config) {
  let validators = null;
  let asyncValidators = null;
  let updateOn;
  if (config != null) {
    if (isAbstractControlOptions(config)) {
      // `extra` are `AbstractControlOptions`
      validators = config.validators != null ? config.validators : null;
      asyncValidators = config.asyncValidators != null ? config.asyncValidators : null;
      updateOn = config.updateOn != null ? config.updateOn : undefined;
    }
    if (isLegacyOrOpts(config)) {
      // `extra` are legacy form group options
      validators = validators || [];
      if (config.validator != null) {
        validators.push(config.validator);
      }
      asyncValidators = asyncValidators || [];
      if (config.asyncValidator != null) {
        asyncValidators.push(config.asyncValidator);
      }
    }
    if (!config.classValidatorOptions) config.classValidatorOptions = CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER;
  }
  return {
    validators,
    asyncValidators,
    updateOn
  };
}
export function validatorsOfType(source, type) {
  return source.filter(func => func.type === type).map(func => func.validator);
}
function collectValidationsForField(validationInfo, fieldName, parentConditionalValidators = []) {
  const {
    allValidationMetadatas,
    validationGroupMetadatas
  } = validationInfo;
  const conditional = [];
  const custom = [];
  const simple = [];
  // All Nested Validation for the field
  const allNested = [];
  // Nested Validation for the field for the requested class-validator group
  const groupBasedNested = [];
  allValidationMetadatas.forEach(validationMetadata => {
    if (ValidationUtils.isPropertyValidatorOfType(validationMetadata, fieldName, ValidationKeys.conditional.type)) {
      conditional.push(validationMetadata);
    } else if (ValidationUtils.isPropertyValidatorOfType(validationMetadata, fieldName, ValidationKeys.nested.type)) {
      allNested.push(validationMetadata);
    } else if (ValidationUtils.isPropertyValidatorOfType(validationMetadata, fieldName, ValidationKeys.custom.type)) {
      custom.push(validationMetadata);
    } else {
      simple.push(validationMetadata);
    }
  });
  validationGroupMetadatas.forEach(validationMetadata => {
    // if (ValidationUtils.isPropertyValidatorOfType(validationMetadata, fieldName, ValidationKeys.conditional.type)) {
    // 	conditional.push(validationMetadata);
    // }
    if (ValidationUtils.isPropertyValidatorOfType(validationMetadata, fieldName, ValidationKeys.nested.type)) {
      groupBasedNested.push(validationMetadata);
    }
  });
  return {
    conditional,
    custom,
    simple,
    allNested,
    groupBasedNested,
    parentConditionalValidators
  };
}
/**
 * add individual FormControl validators, required to mark the FormControl valid/invalid.
 * The error messages will be generated separately and made available on parentFormGroup.formErrors.
 */
export function createValidatorsForField(validationInfo, fieldDefinition, fields, validatorOptions, parentConditionalValidators = []) {
  const validations = collectValidationsForField(validationInfo, fieldDefinition.name, parentConditionalValidators);
  const {
    allValidationMetadatas,
    validator,
    customValidator
  } = validationInfo;
  const fieldName = fieldDefinition.name;
  const validationDefinitions = [];
  const validationFunctions = [];
  allValidationMetadatas.filter(meta => {
    // filter out conditional validation. Conditional checks will be run as pre-checks within the other validators.
    // this is because angular does not support conditional validation natively.
    if (meta.type === ValidationKeys.conditional.type) return false;
    // for some reason... ensure meta targets the actual field
    return meta.propertyName === fieldName;
  }).forEach(validationMetadata => {
    // Add all validation to the field except the @NestedValidation definition as
    // being part of the form would imply it is validated if any other rules are present
    if (validationMetadata.type !== ValidationKeys.nested.type) {
      validationDefinitions.push(validationMetadata);
    }
    for (const typeKey in ValidationTypes) {
      // See comments at createNestedValidate() to understand why this has been commented out.
      if (ValidationUtils.isNestedValidate(validationMetadata, typeKey, validator)) {
        // const fieldIsFormGroupOrArray = (fields?.[fieldName] instanceof DynamicFormGroup) || (fields?.[fieldName] instanceof FormArray);
        // const hasOnlyGroupBasedNested = validations.allNested.length === validations.groupBasedNested.length;
        // const hasOnlyNonGroupBasedNested = validations.allNested.length > 0 && validations.groupBasedNested.length === 0;
        // if (hasOnlyGroupBasedNested || (fieldIsFormGroupOrArray && hasOnlyNonGroupBasedNested)) {
        // 	const objectToValidate = fields[fieldName] instanceof DynamicFormGroup ? fields[fieldName].getObject() : undefined;
        // 	const nestedValidate = createNestedValidate(fieldName, objectToValidate, validationMetadata, validations, validatorOptions);
        // 	validationFunctions.push(nestedValidate);
        // }
      }
      // Handle Custom Validation
      else if (ValidationUtils.isCustomValidate(validationMetadata, typeKey, validator)) {
        const customValidation = createCustomValidation(fieldName, validationMetadata, validations, validatorOptions);
        validationFunctions.push(customValidation);
      }
      // Handle remaining validation
      else if (ValidationUtils.isDynamicValidate(validationMetadata, typeKey, validator)) {
        const dynamicValidate = createDynamicValidate(fieldName, validationMetadata, validations, validatorOptions, customValidator);
        validationFunctions.push(dynamicValidate);
      }
    }
  });
  return {
    validationDefinitions,
    validationFunctions,
    fieldValidations: validations
  };
}
export function addValidatorsToFieldDefinition(fieldDefinition, validationDefinitions, validationFunctions, fieldValidations) {
  if (fieldValidations.parentConditionalValidators.length) {
    fieldDefinition.validationFlags.conditional = true;
    fieldDefinition.validationFlags.parentConditional = true;
  }
  if (fieldValidations.conditional.length) fieldDefinition.validationFlags.conditional = true;
  if (fieldValidations.custom.length) fieldDefinition.validationFlags.custom = true;
  if (fieldValidations.allNested.length) fieldDefinition.validationFlags.nested = true;
  fieldDefinition.validationDefinitions.push(...validationDefinitions);
  fieldDefinition.validationFunctions.push(...validationFunctions);
}
/**
 * NOTE: createNestedValidate was rewritten to execute correctly on formGroups and FormArrays
 * based on presence of @ValidateNested decorator.However, its validation result will always be null.
 * Removing it doesn't seem to degrade anything, validation stays reactive too.
 * So it seems its not needed anymore.
 * Possibly it is responsible for a validation case thats not present anywhere right now.
 * e.g. raw subobjects, formArray containing primitives or whatever.
 * If any nested validation isn't working as expected, this may be a thing to check first.
 */
// function createNestedValidate(
// 	fieldName: string,
// 	objectToValidate: any,
// 	validationMetadata: ValidationMetadata,
// 	validations: FieldValidationsGrouped,
// 	validatorOptions?: ValidatorOptions
// ): ValidatorFunctionType {
// 	return {
// 		type: 'async',
// 		validator: function(control: FormControl) {
// 			if(!evaluateConditionalValidationResult(control,validations)) {
// 				return of(null)
// 			}
// 			// const dataToValidate = objectToValidate !== undefined ? objectToValidate : control.value;
// 			const dataToValidate = control.value;
// 			// console.log('exec nestedValidate', fieldName, control, validationMetadata)
// 			const result = ValidationUtils.getValidateErrors(
// 				fieldName,
// 				control,
// 				dataToValidate,
// 				validatorOptions
// 			).pipe(
// 				// tap(errors=>console.log('nestedValidate errors',errors)),
// 				map(errors => Object.keys(errors).length === 0),
// 				map(validateState => ValidationUtils.getIsValidResult(validateState, validationMetadata, 'nestedValidate')),
// 			);
// 			return result
// 		}
// 	};
// }
function createDynamicValidate(fieldName, validationMetadata, validations, validatorOptions, customValidator) {
  return {
    type: 'async',
    validator: function (control) {
      if (!control) return of(null);
      if (!evaluateConditionalValidationResult(control, validations)) {
        return of(null);
      }
      let isValid = true;
      function convertToArray(val) {
        if (val instanceof Map) {
          return Array.from(val.values());
        }
        return Array.isArray(val) ? val : Array.from(val);
      }
      if (control.parent && control.parent.value) {
        const object = control.parent.value;
        const value = control.value;
        if (validationMetadata.each) {
          if (value instanceof Array || value instanceof Set || value instanceof Map) {
            const arrayValue = convertToArray(value);
            isValid = arrayValue.every(function (subValue) {
              const result = customValidator.validatePropertyByMetadata(object, value, fieldName, [validationMetadata]);
              return result.length === 0;
            });
          }
        } else {
          const result = customValidator.validatePropertyByMetadata(object, value, fieldName, [validationMetadata]);
          isValid = result.length === 0;
        }
      }
      let validateState$ = of(isValid);
      if (!isValid && validations.conditional.length > 0) {
        validateState$ = ValidationUtils.setObjectValueAndGetValidationErrors(fieldName, control, validatorOptions).pipe(map(validateErrors => validateErrors ? !!validateErrors[fieldName] : false));
      }
      return validateState$.pipe(map(validateState => {
        return ValidationUtils.getIsValidResult(validateState, validationMetadata, 'dynamicValidate');
      }));
    }
  };
}
function createCustomValidation(fieldName, validationMetadata, validations, validatorOptions) {
  return {
    type: 'async',
    validator: function (control) {
      if (!evaluateConditionalValidationResult(control, validations)) {
        return of(null);
      }
      return ValidationUtils.setObjectValueAndGetValidationErrors(fieldName, control, validatorOptions).pipe(map(errors => {
        const allRelatedErrors = Object.keys(errors).filter(key => errors[key][fieldName] || errors[fieldName]);
        const valid = ValidationUtils.getIsValidResult(allRelatedErrors.length === 0, validationMetadata, 'customValidation');
        return valid;
      }));
    }
  };
}
function findParentByModel(form, modelType) {
  let curr = form;
  while (curr instanceof DynamicFormGroup || curr instanceof DynamicFormArray) {
    // validator.target is base model, parents 
    if (curr.factoryModel && getBaseModelFromEditorModel(curr.factoryModel) === modelType) {
      return curr;
    }
    curr = curr.parent;
  }
  return null;
}
/**
 * conditional validation checks are being inherited to child form group props.
 * This is for compatibility reasons because ng formcontrols have no built-in hierarchical validation conditions.
 * There's one tricky thing: An inherited conditional validation check expects to receive the parent form value,
 * while a validation defined on the prop itself expects to work with the "local" form value.
 * fortunately, the ValidationMetadata entries contain info on which model they expect to receive.
 */
function evaluateConditionalValidationResult(control, validations) {
  if (validations.parentConditionalValidators.length > 0) {
    if (!evaluateParentConditionalValidation(control, validations.parentConditionalValidators)) return false;
  }
  if (validations.conditional.length > 0) {
    if (!evaluateLocalConditionalValidation(control, validations.conditional)) return false;
  }
  return true;
}
export function evaluateParentConditionalValidation(control, parentConditionalValidators) {
  // during formGroup creation, the control has no parent.
  // as validation concept is to opt-in for validation, the best reaction here is to let it skip.
  if (!control.parent) return false;
  const controlFg = control.parent;
  let func;
  const parentConditionalResult = (() => {
    for (const validator of parentConditionalValidators) {
      if (typeof validator.target === 'string') throw new Error('Received validator target is a string. Dont know how to handle such a case!');
      const targetForm = findParentByModel(controlFg, validator.target);
      if (!targetForm) throw new Error('conditional validation failed: found no formGroup to validate against. validation target was: ' + validator.target);
      for (func of validator.constraints) {
        if (typeof func === 'function' && !func(targetForm.value, targetForm.value[validator.propertyName])) {
          return false;
        }
      }
    }
    return true;
  })();
  // console.log('parentConditionalValidators check for '+getControlName(control),' validation required = ', parentConditionalResult)
  return parentConditionalResult;
}
export function evaluateLocalConditionalValidation(control, conditionalValidators) {
  // during formGroup creation, the control has no parent.
  // as validation concept is to opt-in for validation, the best reaction here is to let it skip.
  if (!control.parent) return false;
  const controlFg = control.parent;
  let func;
  for (const validator of conditionalValidators) {
    for (func of validator.constraints) {
      if (typeof func === 'function' && !func(controlFg.value, controlFg.value[validator.propertyName])) {
        return false;
      }
    }
  }
  return true;
}